import {
  type IndustrialCaseDocument,
  selectDisplayedIndustrialCaseOutcomes,
} from '@/domain/industrialCase/industrialCaseDocument';
import type { IndustrialCaseSummary } from '@/domain/industrialCase/industrialCaseSummary';
import Link from '@/routing/AppLink';
import { useTranslation } from 'next-i18next/pages';

import { IconArrowRightRound } from '@/components/apps/ui/icon';

import { IndustrialCaseArticleBody } from './IndustrialCaseArticleBody';
import { IndustrialCaseClosingCta } from './IndustrialCaseClosingCta';
import * as S from './IndustrialCaseDetailPage.styles';
import { IndustrialCaseMetricSidebar } from './IndustrialCaseMetricSidebar';
import IndustrialCaseParallaxHero from './IndustrialCaseParallaxHero';
import { IndustrialCaseRelatedCases } from './IndustrialCaseRelatedCases';
import IndustrialCaseScrollProgress from './IndustrialCaseScrollProgress';
import { IndustrialCaseShareActions } from './IndustrialCaseShareActions';
import {
  buildIndustrialCaseArticleLabels,
  buildIndustrialCaseShareLabels,
} from './industrialCaseDetailLabels';
import type { IndustrialCaseSelectionReporter } from './useIndustrialCaseAnalytics';

type Props = {
  /**
   * The published case, already narrowed to the language being rendered.
   *
   * Prose comes from the document as authored, so nothing here goes through
   * `t()`. Only the surrounding chrome is translated.
   */
  caseDocument: IndustrialCaseDocument;
  /** Keeps public-page visuals while disabling navigation and share effects. */
  isPreview?: boolean;
  /**
   * Cases offered at the end of this one, chosen on the server. Empty in the
   * admin preview, which has no published neighbours to read.
   */
  relatedCases?: ReadonlyArray<IndustrialCaseSummary>;
  /** Reports clicks on this case's surfaces. Absent in the admin preview. */
  onSelectContent?: IndustrialCaseSelectionReporter;
};

/**
 * ### IndustrialCaseDetailPage
 *
 * Renders the complete customer-case composition used by both the public route
 * and the admin preview. Preview mode keeps the same hierarchy and chrome but
 * turns external actions into inert visual controls.
 *
 * @param props Customer-case document and interaction mode
 * @returns The complete customer-case detail view
 */
export function IndustrialCaseDetailPage({
  caseDocument,
  isPreview = false,
  relatedCases = [],
  onSelectContent,
}: Props) {
  const { i18n, t } = useTranslation('common');
  const translate = isPreview
    ? i18n.getFixedT(caseDocument.language, 'common')
    : t;
  const articleLabels = buildIndustrialCaseArticleLabels(translate);
  const shareLabels = buildIndustrialCaseShareLabels(translate);
  const breadcrumbContent = (
    <>
      <span className={S.breadcrumbIcon}>
        <IconArrowRightRound
          className={S.breadcrumbIconGlyph}
          size={20}
          aria-hidden="true"
        />
      </span>
      <span className={S.breadcrumbLabel}>{translate('고객 사례')}</span>
    </>
  );

  return (
    <div
      className={S.pageSection}
      data-industrial-case-content
      data-industrial-case-preview={isPreview ? '' : undefined}
    >
      <IndustrialCaseScrollProgress label={translate('콘텐츠 읽기 진행률')} />

      <div className={S.shell}>
        <div className={S.gridGuides} aria-hidden="true">
          <span className="left-1/4" />
        </div>

        <div className={S.breadcrumbRow}>
          {isPreview ? (
            <span className={S.breadcrumb}>{breadcrumbContent}</span>
          ) : (
            <Link
              className={S.breadcrumb}
              href="/solution/industrial"
              prefetch={false}
            >
              {breadcrumbContent}
            </Link>
          )}
        </div>

        <header>
          <IndustrialCaseParallaxHero
            customerName={caseDocument.customerName}
            image={caseDocument.heroImage}
            meta={caseDocument.meta}
            classification={caseDocument}
          />

          <div className={S.introStage}>
            <div className={S.introContent}>
              <p className={S.soundLabel}>{caseDocument.targetSound}</p>
              <h1 className={S.title}>{caseDocument.title}</h1>
              <p className={S.lead}>{caseDocument.summary}</p>
            </div>
          </div>
        </header>

        <div className={S.contentGrid}>
          <IndustrialCaseMetricSidebar
            caseId={caseDocument.slug}
            metric={caseDocument.primaryMetric}
            outcomes={selectDisplayedIndustrialCaseOutcomes(
              caseDocument.outcomes,
            )}
            inquiryLabel={translate('문의하기')}
            metricLabel={translate('핵심 지표')}
            isInteractive={!isPreview}
          />

          <article className={S.article}>
            {/* Shared with the admin preview so both render the same document */}
            <IndustrialCaseArticleBody
              document={caseDocument}
              labels={articleLabels}
            />

            <IndustrialCaseShareActions
              isInteractive={!isPreview}
              labels={shareLabels}
              onAskAi={
                onSelectContent
                  ? (service) => onSelectContent('ask_ai', service)
                  : undefined
              }
            />
          </article>
        </div>

        <IndustrialCaseRelatedCases
          cases={relatedCases}
          title={translate('관련 고객 사례')}
          readLabel={translate('사례 읽기')}
          metricLabel={translate('핵심 지표')}
          onSelect={
            onSelectContent
              ? (slug) => onSelectContent('case_card', slug)
              : undefined
          }
        />

        <IndustrialCaseClosingCta
          caseId={caseDocument.slug}
          title={translate('우리 생산 라인에 적용 가능한지 확인해보세요')}
          description={translate(
            '검사 대상 소리와 생산 조건을 알려주시면 적용 가능 범위와 필요한 데이터를 함께 정리해 드립니다',
          )}
          inquiryLabel={translate('문의하기')}
          backLabel={translate('ListenAI Industrial 소개로 돌아가기')}
          isInteractive={!isPreview}
          onInquiry={
            onSelectContent
              ? () => onSelectContent('case_cta', caseDocument.slug)
              : undefined
          }
        />
      </div>
    </div>
  );
}
